import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

const isVelicyaMobileMode = () => window.matchMedia('(max-width: 989px), (min-width: 990px) and (max-width: 1366px) and (pointer: coarse)').matches;

/**
 * VELICYA + HORIZON
 * Desktop : fonctionnement Horizon original.
 * Mobile <= 989px : le hamburger Horizon ouvre directement
 * le tiroir mobile VELICYA.
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    document.addEventListener('click', (event) => {
  if (!isVelicyaMobileMode()) return;

  const closeButton = event.target.closest(
    '[data-velicya-mobile-close]'
  );

  if (!closeButton) return;

  event.preventDefault();
  event.stopPropagation();

  this.#closeVelicyaMobile();
});
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    if (isVelicyaMobileMode()) {
      this.#closeVelicyaMobile();
      return;
    }

    this.#close(this.#getDetailsElement(event));
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) {
      return this.refs.details;
    }

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Ouvre directement le menu mobile VELICYA.
   */
  #openVelicyaMobile() {
    const mobile = document.querySelector('[data-velicya-mobile]');

    if (!mobile) {
      return false;
    }

    /*
     * On remet d'abord le drawer Horizon dans son état fermé.
     */
    const details = this.refs.details;

    details.classList.remove('menu-open');
    details.removeAttribute('open');

    const horizonSummary = details.querySelector('summary');

    if (horizonSummary) {
      horizonSummary.setAttribute('aria-expanded', 'false');
    }

    /*
     * Ouverture du tiroir VELICYA.
     */
    mobile.classList.add('is-open');
    mobile.setAttribute('aria-hidden', 'false');

    mobile.style.setProperty(
      'visibility',
      'visible',
      'important'
    );

    mobile.style.setProperty(
      'pointer-events',
      'auto',
      'important'
    );

    const drawer = mobile.querySelector(
      '.velicya-mobile__drawer'
    );

    if (drawer) {
      drawer.style.setProperty(
        'transform',
        'translateX(0)',
        'important'
      );
    }

    const backdrop = mobile.querySelector(
      '.velicya-mobile__backdrop'
    );

    if (backdrop) {
      backdrop.style.setProperty(
        'opacity',
        '1',
        'important'
      );
    }

    document.body.classList.add(
      'velicya-mobile-lock'
    );

    const velicyaTrigger = document.querySelector(
      '[data-velicya-mobile-open]'
    );

    if (velicyaTrigger) {
      velicyaTrigger.setAttribute(
        'aria-expanded',
        'true'
      );
    }

    return true;
  }

  /**
   * Ferme le menu mobile VELICYA.
   */
  #closeVelicyaMobile() {
    const mobile = document.querySelector(
      '[data-velicya-mobile]'
    );

    if (!mobile) return;

   mobile.classList.remove('is-open');
mobile.setAttribute('aria-hidden', 'true');

mobile.style.setProperty('visibility', 'hidden', 'important');
mobile.style.setProperty('pointer-events', 'none', 'important');

    const drawer = mobile.querySelector(
      '.velicya-mobile__drawer'
    );

    if (drawer) {
      drawer.style.removeProperty('transform');
    }

    const backdrop = mobile.querySelector(
      '.velicya-mobile__backdrop'
    );

    if (backdrop) {
      backdrop.style.removeProperty('opacity');
    }

    document.body.classList.remove(
      'velicya-mobile-lock'
    );
    document.documentElement.classList.remove('velicya-mobile-lock');

document.body.style.removeProperty('overflow');
document.body.style.removeProperty('position');
document.body.style.removeProperty('width');
document.body.style.removeProperty('height');
document.body.style.removeProperty('top');
document.body.style.removeProperty('touch-action');

document.documentElement.style.removeProperty('overflow');
document.documentElement.style.removeProperty('height');
document.documentElement.style.removeProperty('touch-action');
removeTrapFocus();
const horizonDetails = this.refs.details;

if (horizonDetails) {
  horizonDetails.classList.remove('menu-open');
  horizonDetails.removeAttribute('open');

  horizonDetails
    .querySelector('summary')
    ?.setAttribute('aria-expanded', 'false');
}

this.refs.menuDrawer?.classList.remove(
  'menu-drawer--has-submenu-opened'
);
    const velicyaTrigger = document.querySelector(
      '[data-velicya-mobile-open]'
    );

    if (velicyaTrigger) {
      velicyaTrigger.setAttribute(
        'aria-expanded',
        'false'
      );
    }
  }

  /**
   * Fonction appelée par :
   * on:click="/toggle"
   */
  toggle() {
    /*
     * MOBILE VELICYA
     *
     * Très important :
     * on ne lance PAS this.open().
     * Donc Horizon ne doit pas ouvrir son panneau orange.
     */
    if (isVelicyaMobileMode()) {
      const mobile = document.querySelector(
        '[data-velicya-mobile]'
      );

      if (mobile) {
        if (mobile.classList.contains('is-open')) {
          this.#closeVelicyaMobile();
        } else {
          this.#openVelicyaMobile();
        }

        return;
      }
    }

    /*
     * DESKTOP :
     * comportement Horizon original.
     */
    return this.isOpen
      ? this.close()
      : this.open();
  }

  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute(
      'aria-expanded',
      'true'
    );

    this.preventInitialAccordionAnimations(details);

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add(
          'menu-drawer--has-submenu-opened'
        );
      }

      const drawer = details.querySelector(
        '.menu-drawer, .menu-drawer__submenu'
      );

      onAnimationEnd(
        drawer || details,
        () => trapFocus(details),
        { subtree: false }
      );
    });
  }

  back(event) {
    this.#close(
      this.#getDetailsElement(event)
    );
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute(
      'aria-expanded',
      'false'
    );

    details.classList.remove('menu-open');

    this.refs.menuDrawer.classList.remove(
      'menu-drawer--has-submenu-opened'
    );

    const drawer = details.querySelector(
      '.menu-drawer, .menu-drawer__submenu'
    );

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);

        if (details === this.refs.details) {
          removeTrapFocus();

          const openDetails =
            this.querySelectorAll(
              'details[open]:not(accordion-custom > details)'
            );

          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  #setupAnimatedElementListeners() {
    const allAnimated =
      this.querySelectorAll(
        '.menu-drawer__animated-element'
      );

    allAnimated.forEach((element) => {
      element.addEventListener(
        'animationend',
        removeWillChangeOnAnimationEnd
      );
    });
  }

  preventInitialAccordionAnimations(details) {
    const content =
      details.querySelectorAll(
        'accordion-custom .details-content'
      );

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add(
          'details-content--no-animation'
        );
      }
    });

    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove(
            'details-content--no-animation'
          );
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define(
    'header-drawer',
    HeaderDrawer
  );
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');

  element
    .querySelector('summary')
    ?.setAttribute(
      'aria-expanded',
      'false'
    );
}